import React, { useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ChatSource } from "../../types";
import { RiArrowDownSLine, RiStackedView } from "@remixicon/react";
import {
  SqDropdownMenu,
  SqDropdownMenuContent,
  SqDropdownMenuItem,
  SqDropdownMenuPortal,
  SqDropdownMenuTrigger,
} from "@/components/squircle-ui/dropdown";

interface SectionSelectorDropdownProps {
  sections: ChatSource[];
  selectedSource: ChatSource | null;
  onSourceChange: (source: ChatSource) => void;
  showIcon?: boolean;
}

const SectionSelectorDropdown: React.FC<SectionSelectorDropdownProps> = ({
  sections,
  selectedSource,
  onSourceChange,
  showIcon = true,
}) => {
  const menuItems: ChatSource[] = useMemo(
    () => [
      {
        id: "auto",
        title: "Auto",
        icon: null,
        sectionIds: [],
      },
      {
        id: "all",
        title: "All Sections",
        icon: null,
        sectionIds: sections.flatMap((section) => section.sectionIds),
      },
      ...sections,
    ],
    [sections],
  );

  useEffect(() => {
    if (!selectedSource) {
      onSourceChange(menuItems[0]);
    }
    if (!selectedSource?.id) {
      const item = menuItems.find(
        (item) =>
          item.sectionIds.length === selectedSource?.sectionIds.length &&
          item.sectionIds.every((id) => selectedSource?.sectionIds.includes(id)),
      );
      if (item) {
        onSourceChange(item);
      }
    }
  }, [menuItems, selectedSource, onSourceChange]);

  return (
    <SqDropdownMenu>
      <SqDropdownMenuTrigger asChild>
        <Button variant="outline" className="max-w-[190px] h-8 border-0">
          <RiStackedView size={14} color="#9AA3BD" className="shrink-0" />
          <span className="truncate text-[12px] text-[rgba(2,24,89,0.8)]">
            {selectedSource?.title || "Auto"}
          </span>
          <RiArrowDownSLine size={14} className="shrink-0" color="#9AA3BD" />
        </Button>
      </SqDropdownMenuTrigger>
      <SqDropdownMenuPortal>
        <SqDropdownMenuContent
          align="start"
          radius={8}
          className="w-[190px] p-0 bg-white shadow-none mb-1 rounded-[8px] border-border-gray flex flex-col justify-center items-start"
        >
          <div className="h-[32px] flex items-center text-[10px] font-medium text-brand-primary-600 px-[9px] py-[6px] tracking-[1px] uppercase leading-[12px]">
            Sections
          </div>
          {menuItems.map((item, index) => (
            <SqDropdownMenuItem
              key={item.id}
              radius={0}
              className={cn(
                "cursor-pointer group h-[32px] flex items-center pl-[9px] py-[6px] pr-[8px] rounded-none w-full mx-[1px] w-[99%]",
                selectedSource?.id === item.id
                  ? "squircle-fill-selected-gray hover:squircle-fill-selected-gray text-brand-primary-800 hover:text-brand-primary-800"
                  : "hover:squircle-fill-hover-gray text-brand-primary-new/70 hover:text-brand-primary-new/70",
                menuItems.length - 1 === index &&
                  "squircle-radius-bottom-left-[8px] squircle-radius-bottom-right-[8px]",
              )}
              onClick={() => onSourceChange(item)}
            >
              {showIcon && item.icon && (
                <span className="shrink-0 mr-1 text-white">{item.icon}</span>
              )}
              <span className="truncate leading-[20px] text-[12px] font-normal">
                {item.title}
              </span>
            </SqDropdownMenuItem>
          ))}
        </SqDropdownMenuContent>
      </SqDropdownMenuPortal>
    </SqDropdownMenu>
  );
};

export default SectionSelectorDropdown;
